// src/components/ChartInputPanel.js

import React, { useState } from "react";
import { BirthDetailsForm } from "./BirthDetailsForm";

function ChartInputPanel({
  form,
  birthDetails,
  locationMode,
  locationErrors,
  result,
  error,
  loading,
  onFormChange,
  onBirthDetailsChange,
  onLocationModeChange,
  onCalculate,
  onReset,
}) {
  const [agreed, setAgreed] = useState(false);
  const [showAgreementHint, setShowAgreementHint] = useState(false);

  const baseInputStyle = {
    width: "100%",
    padding: "5px 7px",
    marginTop: 2,
    borderRadius: 6,
    border: "1px solid #4b5563",
    background: "#020617",
    color: "#e5e7eb",
    fontSize: 13,
    boxSizing: "border-box",
  };

  const disabledInputStyle = {
    background: "#020617",
    color: "#9ca3af",
    cursor: "not-allowed",
    opacity: 0.8,
  };

  const handleGenerate = () => {
    if (!agreed) {
      setShowAgreementHint(true);
      return;
    }

    setShowAgreementHint(false);
    onCalculate();
  };

  return (
    <div
      style={{
        background: "rgba(15,23,42,0.95)",
        border: "1px solid rgba(148,163,184,0.4)",
        borderRadius: 10,
        padding: 12,
        boxSizing: "border-box",
      }}
    >
      {/* Name */}
      <div style={{ marginBottom: 6 }}>
        <label style={{ fontSize: 13, display: "block" }}>
          Name:
          <input
            name="name"
            value={form.name}
            onChange={onFormChange}
            placeholder="Your Name Here"
            style={baseInputStyle}
          />
        </label>
      </div>

      {/* Birth details */}
      <BirthDetailsForm
        value={birthDetails}
        onChange={onBirthDetailsChange}
        locationMode={locationMode}
        onLocationModeChange={onLocationModeChange}
        locationErrors={locationErrors}
      />

      {/* Country */}
      <div style={{ marginTop: 6 }}>
        <label style={{ fontSize: 13, display: "block" }}>
          Country:
          <input
            name="country"
            disabled={locationMode === "coords"}
            value={form.country}
            onChange={onFormChange}
            placeholder="Your Country"
            style={{
              ...baseInputStyle,
              ...(locationMode === "coords" ? disabledInputStyle : {}),
            }}
          />
        </label>
      </div>

      {/* Consent Section */}
      <div
        style={{
          marginTop: 14,
          fontSize: 12.5,
          color: "#cbd5e1",
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          lineHeight: 1.5,
        }}
      >
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => {
            setAgreed(e.target.checked);
            if (e.target.checked) setShowAgreementHint(false);
          }}
          style={{
            marginTop: 3,
            accentColor: "#fbbf24",
            cursor: "pointer",
          }}
        />

        <span>
          I understand this report is offered for awareness and reflection,
          not prediction or instruction. I agree to the{" "}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#fbbf24",
              textDecoration: "none",
            }}
          >
            Terms & Responsible Use
          </a>.
        </span>
      </div>

      {/* Gentle Hint */}
      {showAgreementHint && (
        <div
          style={{
            marginTop: 6,
            fontSize: 11.5,
            color: "#94a3b8",
          }}
        >
          Please confirm responsible use before generating your chart.
        </div>
      )}

      {/* Buttons */}
      <div
        style={{
          marginTop: 14,
          display: "flex",
          gap: 8,
          alignItems: "center",
        }}
      >
        <button
          onClick={handleGenerate}
          disabled={!agreed || loading}
          style={{
            padding: "7px 16px",
            borderRadius: 999,
            border: "none",
            background: "linear-gradient(90deg, #f97316, #facc15)",
            color: "#111827",
            fontWeight: 600,
            cursor: agreed && !loading ? "pointer" : "not-allowed",

            /* Smooth State Transition */
            opacity: agreed && !loading ? 1 : 0.55,
            transform: agreed && !loading ? "translateY(0px)" : "translateY(1px)",
            boxShadow: agreed && !loading
              ? "0 6px 18px rgba(251,191,36,0.35)"
              : "0 2px 6px rgba(0,0,0,0.3)",

            transition:
              "opacity 0.25s ease, transform 0.25s ease, box-shadow 0.35s ease",
          }}
        >
          {loading ? "Generating..." : "Generate My Chart"}
        </button>

        <button
          type="button"
          onClick={onReset}
          style={{
            padding: "7px 16px",
            borderRadius: 999,
            border: "1px solid #4b5563",
            background: "transparent",
            color: "#e5e7eb",
            fontWeight: 500,
            cursor: "pointer",
            fontSize: 13,
          }}
        >
          Reset
        </button>
      </div>

      {/* Top-level error */}
      {error && (
        <div
          style={{
            marginTop: 10,
            fontSize: 12,
            color: "#f97373",
            whiteSpace: "pre-wrap",
          }}
        >
          {error}
        </div>
      )}

      {/* Location info */}
      {result && (
        <div
          style={{
            marginTop: 12,
            fontSize: 11,
            color: "#9ca3af",
            borderTop: "1px solid rgba(55,65,81,0.8)",
            paddingTop: 8,
          }}
        >
          {locationMode === "city" && result.location_resolved && (
            <div>
              Using location:&nbsp;
              <span style={{ color: "#e5e7eb" }}>
                {result.location_resolved}
              </span>
            </div>
          )}

          {locationMode === "city" &&
            typeof result.latitude === "number" &&
            typeof result.longitude === "number" && (
              <div>
                Lat: {result.latitude.toFixed(4)} · Lon:{" "}
                {result.longitude.toFixed(4)}
              </div>
            )}

          {locationMode === "coords" &&
            birthDetails.lat &&
            birthDetails.lon && (
              <div>
                Using coordinates from form.
                <br />
                Lat: {birthDetails.lat} · Lon: {birthDetails.lon}
              </div>
            )}
        </div>
      )}
    </div>
  );
}

export default ChartInputPanel;